import { ReactNode, useState } from 'react';

import { useIsMobileDevice } from '../../helpers/hooks/useIsMobileDevice';

import { PostViewModalDesktop } from './PostViewModalDesktop';
import { PostsViewerDesktop } from './components/PostsViewerDesktop/PostsViewerDesktop';
import { PostsViewerTouchDevice } from './components/PostsViewerTouchDevice/PostsViewerTouchDevice';
import { Post } from './types/Post';

interface PostsViewerProps {
  posts: Post[];
  currentPostViewerId: string;
  setCurrentPostViewerId: (catchId: string) => Promise<void>;
  hasMore: boolean;
  isPostViewerOpen: boolean;
  loadMore: () => void;
  onClosePostsViewer: (catchPostId?: string) => Promise<void>;
  showMoreTeaser?: ReactNode;
}

/**
 * Component for viewing a list of posts. It adapts its layout based on the device type (mobile or desktop).
 *
 * @component
 * @param {Post[]} posts - Array of post objects to be displayed.
 * @param {string} currentPostViewerId - ID of the currently viewed post.
 * @param {(catchId: string) => Promise<void>} setCurrentPostViewerId - Function to set the ID of the currently viewed post.
 * @param {boolean} hasMore - Flag indicating if there are more posts to load.
 * @param {boolean} isPostViewerOpen - Flag indicating if the post viewer is open.
 * @param {() => void} loadMore - Function to load more posts.
 * @param {(catchPostId?: string) => Promise<void>} onClosePostsViewer - Function to handle closing the post viewer.
 * @param {ReactNode} [showMoreTeaser] - Optional teaser element to show when there are more posts to load.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * On desktop, it displays a modal with a carousel of posts. On mobile, it displays a full-screen instagram style
 * list of posts.
 *
 * It shows a different layout depending on if the post has a catch attached to it. If it does, it shows an catch view of the post
 * with catch details and a map. All catch details are shown for logged in user with certain feature only shown for premium users.
 * Non logged in users will see a register wall.
 *
 * It includes logic to update the url based on the postviewer being open and the currently viewed post.
 */
export const PostsViewer = ({
  posts,
  currentPostViewerId,
  setCurrentPostViewerId,
  hasMore,
  isPostViewerOpen,
  loadMore,
  onClosePostsViewer: handleClose,
  showMoreTeaser = null,
}: PostsViewerProps) => {
  const isMobileDevice = useIsMobileDevice();
  const [showTeaser, setShowTeaser] = useState(false);
  const currentIndex = posts.findIndex(item => item._id === currentPostViewerId);
  const currentPostId = posts[currentIndex]?._id;

  const handleNextClick = (): void => {
    if (posts[currentIndex + 1]) {
      void setCurrentPostViewerId(posts[currentIndex + 1]._id);
    } else {
      // we should only hit this if we have the showMoreTeaser and we are at the
      // end of the list of posts
      setShowTeaser(true);
    }

    if (hasMore) {
      loadMore();
    }
  };

  const handlePrevClick = (): void => {
    if (showTeaser) {
      setShowTeaser(false);
    } else {
      void setCurrentPostViewerId(posts[currentIndex - 1]._id);
    }
  };

  const desktopHasNext = !showTeaser && (!!posts[currentIndex + 1] || showMoreTeaser);

  return (
    <>
      {isMobileDevice ? (
        <PostsViewerTouchDevice
          posts={posts}
          currentPostId={currentPostViewerId}
          setCurrentPostId={setCurrentPostViewerId}
          hasMore={hasMore}
          loadMore={loadMore}
          onClose={() => {
            void handleClose();
          }}
          showMoreTeaser={showMoreTeaser}
        />
      ) : (
        currentPostId && (
          <PostViewModalDesktop
            hasNext={desktopHasNext}
            hasPrevious={!!posts[currentIndex - 1]}
            isOpen={isPostViewerOpen}
            onClose={() => {
              void handleClose();
            }}
            onNextClicked={handleNextClick}
            onPreviousClicked={handlePrevClick}
          >
            {showMoreTeaser && showTeaser && (
              <div className="bg-sea-foam flex h-full items-center justify-center rounded-lg">
                {showMoreTeaser}
              </div>
            )}
            {!showTeaser && currentPostId && (
              <PostsViewerDesktop posts={posts} currentPostId={currentPostId} />
            )}
          </PostViewModalDesktop>
        )
      )}
    </>
  );
};
